import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { BookOpen, Search, ArrowRight, FileText } from 'lucide-react';

export const ResourcesPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCat, setSelectedCat] = useState<string>('ALL');

  const { data: resourcesRes, isLoading } = useQuery({
    queryKey: ['public-resources', { category: selectedCat, search: searchTerm }],
    queryFn: () => {
      let url = '/public/resources';
      const params = new URLSearchParams();
      if (selectedCat !== 'ALL') params.append('category', selectedCat);
      if (searchTerm) params.append('search', searchTerm);
      if (params.toString()) url += `?${params.toString()}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const resources = resourcesRes || [];

  return (
    <div className="space-y-16 py-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      
      {/* Header */}
      <section className="text-center space-y-4 max-w-3xl mx-auto">
        <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
          Knowledge &amp; Editorial Guides
        </span>
        <h1 className="text-4xl sm:text-5xl font-serif font-bold text-editorial-dark">
          Publishing Resources &amp; Guides
        </h1>
        <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
          Explore our library of author resources, manuscript preparation guidelines, theological style rules, and publishing industry insights.
        </p>
      </section>

      {/* Search & Filter Bar */}
      <section className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-xl bg-white border border-editorial-border shadow-xs">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search guides and resources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-slate-50 border border-slate-200 text-xs focus:outline-hidden focus:border-brand-500"
          />
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          {['ALL', 'WRITING', 'PUBLISHING_GUIDES', 'EDITORIAL'].map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCat(cat)}
              className={`px-3 py-1.5 rounded-md font-semibold transition-colors ${
                selectedCat === cat
                  ? 'bg-editorial-dark text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {cat === 'ALL' ? 'All Resources' : cat.replace('_', ' ')}
            </button>
          ))}
        </div>
      </section>

      {/* Resources Grid */}
      <section>
        {isLoading ? (
          <div className="text-center py-16 text-xs font-mono text-slate-500">Loading resources...</div>
        ) : resources.length === 0 ? (
          <div className="text-center py-16 text-slate-500 text-sm">No resources found matching your query.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {resources.map((res: any) => (
              <div
                key={res.id}
                className="p-8 rounded-2xl bg-white border border-editorial-border hover:border-brand-300 hover:shadow-editorial-lg transition-all flex flex-col justify-between space-y-6"
              >
                <div className="space-y-3">
                  <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-brand-700">
                    {res.category.replace('_', ' ')}
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-slate-900">
                    {res.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-sans">
                    {res.summary}
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-slate-400 font-medium">By {res.author}</span>
                  <Link
                    to={`/resources/${res.slug}`}
                    className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1"
                  >
                    <span>Read Full Guide</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

    </div>
  );
};
